import { useState, type ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { ArrowRight, ExternalLink } from 'lucide-react';

const portalCtaDef: SlotGroupDefinition = {
  label: 'Investor Portal Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.alapts.com/investors' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Investor Portal' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const strategyLinkDef: SlotGroupDefinition = {
  label: 'Strategy Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/strategy' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Explore our full strategy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const panelImage1Def: SlotGroupDefinition = {
  label: 'Who We Are Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'American Landmark community' },
  },
};

const panelImage2Def: SlotGroupDefinition = {
  label: 'What We Do Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Renovated apartment community' },
  },
};

const panelImage3Def: SlotGroupDefinition = {
  label: 'Our Strategy Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Sun Belt multifamily investment' },
  },
};

const EYEBROW_CLASS = 'font-heading text-sm font-semibold uppercase tracking-[0.14em]';
const H2_CLASS =
  'font-heading text-[clamp(32px,4vw,58px)] font-semibold leading-[1.04] tracking-[-0.03em]';

type SlotData = Record<string, string | null | undefined>;

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <div className="mb-5 flex items-center gap-2">
      <span className="h-2 w-2 shrink-0 rounded-[3px] bg-[rgb(var(--ala-accent))]" aria-hidden />
      {children}
    </div>
  );
}

function PortalButton({ data }: { data: SlotData }) {
  return (
    <a
      href={data.url || 'https://www.alapts.com/investors'}
      target={data._target || '_blank'}
      rel="noopener noreferrer"
      className="group inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-[rgb(var(--ala-secondary))] px-7 py-4 font-heading text-sm font-semibold uppercase tracking-wide text-[rgb(var(--ala-on-secondary))] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(84,158,255,0.45)]"
    >
      {data.label || 'Investor Portal'}
      <ExternalLink className="h-4 w-4 transition-transform group-hover:rotate-12" aria-hidden />
    </a>
  );
}

function StrategyPanel({
  index,
  active,
  onActivate,
  image,
  dark = false,
  children,
}: {
  index: number;
  active: boolean;
  onActivate: () => void;
  image: ReactNode;
  dark?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={active}
      onMouseEnter={onActivate}
      onFocus={onActivate}
      onClick={onActivate}
      className={`group relative flex min-h-[440px] cursor-pointer flex-col justify-end overflow-hidden rounded-3xl outline-none ring-[rgb(var(--ala-secondary))] transition-[flex-grow] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] focus-visible:ring-2 lg:min-h-0 lg:basis-0 ${
        active ? 'lg:grow-[3.2]' : 'lg:grow'
      }`}
    >
      <div className="absolute inset-0">{image}</div>
      <div
        className={`absolute inset-0 transition-opacity duration-500 ${
          dark
            ? 'bg-[linear-gradient(180deg,rgb(var(--ala-primary)/0.35)_0%,rgb(var(--ala-primary)/0.95)_70%)]'
            : 'bg-[linear-gradient(180deg,rgb(var(--ala-dark)/0.1)_0%,rgb(var(--ala-dark)/0.9)_75%)]'
        } ${active ? 'opacity-100' : 'opacity-90'}`}
      />
      <div className="relative z-10 flex flex-col gap-4 p-7 md:p-9">
        <span className="font-heading text-sm font-semibold tabular-nums text-[rgb(var(--ala-secondary))]">
          {String(index + 1).padStart(2, '0')}
        </span>
        {children}
      </div>
    </div>
  );
}

export function InvestorsApproach() {
  const portal = useSlotGroup('investorsapproach.portal_cta', portalCtaDef);
  const strategyLink = useSlotGroup('investorsapproach.strategy_link', strategyLinkDef);
  const panelImage1 = useSlotGroup('investorsapproach.panel_image_1', panelImage1Def);
  const panelImage2 = useSlotGroup('investorsapproach.panel_image_2', panelImage2Def);
  const panelImage3 = useSlotGroup('investorsapproach.panel_image_3', panelImage3Def);
  const [activePanel, setActivePanel] = useState(0);

  const panelBodyClass = (i: number) =>
    `grid transition-all duration-500 ${
      activePanel === i ? 'lg:grid-rows-[1fr] lg:opacity-100' : 'lg:grid-rows-[0fr] lg:opacity-0'
    }`;

  return (
    <section id="strategy" className="w-full bg-[rgb(var(--ala-background))] py-20 md:py-28">
      <div className="mx-auto max-w-[1440px] px-6 md:px-[clamp(16px,3.3vw,48px)]">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:items-end">
          <div>
            <Eyebrow>
              <RichTextContent
                path="investorsapproach.strategy_eyebrow"
                label="Strategy Eyebrow"
                as="span"
                className={`${EYEBROW_CLASS} text-[rgb(var(--ala-text-muted))]`}
              />
            </Eyebrow>
            <RichTextContent
              path="investorsapproach.strategy_headline"
              label="Strategy Headline"
              as="h2"
              className={`${H2_CLASS} text-[rgb(var(--ala-text))] [&_em]:not-italic [&_em]:text-[rgb(var(--ala-secondary))]`}
            />
          </div>
          <RichTextContent
            path="investorsapproach.strategy_intro"
            label="Strategy Intro"
            as="p"
            className="font-body text-lg leading-relaxed text-[rgb(var(--ala-text-muted))]"
          />
        </div>

        <div className="mt-12 flex flex-col gap-4 lg:h-[560px] lg:flex-row">
          <StrategyPanel
            index={0}
            active={activePanel === 0}
            onActivate={() => setActivePanel(0)}
            image={
              <EditableSlotGroup
                groupPath="investorsapproach.panel_image_1"
                definition={panelImage1Def}
                className="block h-full w-full"
              >
                <VibesitesImage
                  url={panelImage1.image || ''}
                  alt={panelImage1.alt || ''}
                  sizes="(max-width: 1024px) 100vw, 60vw"
                  className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105"
                />
              </EditableSlotGroup>
            }
          >
            <RichTextContent
              path="investorsapproach.who_title"
              label="Who We Are Title"
              as="h3"
              className="font-heading text-[clamp(26px,2.4vw,36px)] font-semibold leading-tight text-[rgb(var(--ala-on-primary))]"
            />
            <div className={panelBodyClass(0)}>
              <div className="overflow-hidden">
                <RichTextContent
                  path="investorsapproach.who_body"
                  label="Who We Are Body"
                  as="div"
                  className="max-w-[560px] font-body text-base leading-relaxed text-[rgb(var(--ala-on-primary))]/80 [&_strong]:font-semibold [&_strong]:text-[rgb(var(--ala-on-primary))]"
                />
              </div>
            </div>
          </StrategyPanel>
          <StrategyPanel
            index={1}
            active={activePanel === 1}
            onActivate={() => setActivePanel(1)}
            image={
              <EditableSlotGroup
                groupPath="investorsapproach.panel_image_2"
                definition={panelImage2Def}
                className="block h-full w-full"
              >
                <VibesitesImage
                  url={panelImage2.image || ''}
                  alt={panelImage2.alt || ''}
                  sizes="(max-width: 1024px) 100vw, 60vw"
                  className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105"
                />
              </EditableSlotGroup>
            }
          >
            <RichTextContent
              path="investorsapproach.what_title"
              label="What We Do Title"
              as="h3"
              className="font-heading text-[clamp(26px,2.4vw,36px)] font-semibold leading-tight text-[rgb(var(--ala-on-primary))]"
            />
            <div className={panelBodyClass(1)}>
              <div className="overflow-hidden">
                <RichTextContent
                  path="investorsapproach.what_body"
                  label="What We Do Body"
                  as="div"
                  className="max-w-[560px] font-body text-base leading-relaxed text-[rgb(var(--ala-on-primary))]/80 [&_strong]:font-semibold [&_strong]:text-[rgb(var(--ala-on-primary))]"
                />
              </div>
            </div>
          </StrategyPanel>
          <StrategyPanel
            index={2}
            active={activePanel === 2}
            onActivate={() => setActivePanel(2)}
            dark
            image={
              <EditableSlotGroup
                groupPath="investorsapproach.panel_image_3"
                definition={panelImage3Def}
                className="block h-full w-full"
              >
                <VibesitesImage
                  url={panelImage3.image || ''}
                  alt={panelImage3.alt || ''}
                  sizes="(max-width: 1024px) 100vw, 60vw"
                  className="h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105"
                />
              </EditableSlotGroup>
            }
          >
            <RichTextContent
              path="investorsapproach.how_title"
              label="Our Strategy Title"
              as="h3"
              className="font-heading text-[clamp(26px,2.4vw,36px)] font-semibold leading-tight text-[rgb(var(--ala-on-primary))]"
            />
            <div className={panelBodyClass(2)}>
              <div className="overflow-hidden">
                <RichTextContent
                  path="investorsapproach.how_body"
                  label="Our Strategy Body"
                  as="div"
                  className="max-w-[560px] font-body text-base leading-relaxed text-[rgb(var(--ala-on-primary))]/80 [&_strong]:font-semibold [&_strong]:text-[rgb(var(--ala-on-primary))]"
                />
              </div>
            </div>
          </StrategyPanel>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
          <EditableSlotGroup groupPath="investorsapproach.portal_cta" definition={portalCtaDef}>
            <PortalButton data={portal} />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="investorsapproach.strategy_link" definition={strategyLinkDef}>
            <a
              href={strategyLink.url || '/strategy'}
              target={strategyLink._target || '_self'}
              className="group inline-flex items-center gap-2 font-heading text-sm font-semibold uppercase tracking-wide text-[rgb(var(--ala-text))] transition-colors hover:text-[rgb(var(--ala-secondary))]"
            >
              {strategyLink.label || 'Explore our full strategy'}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden />
            </a>
          </EditableSlotGroup>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'InvestorsApproach',
  displayName: 'Investors Approach',
  tokens: {
    'investorsapproach.strategy_eyebrow': {
      label: 'Strategy Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Our Investment Approach</p>',
    },
    'investorsapproach.strategy_headline': {
      label: 'Strategy Headline',
      type: 'richtext',
      defaultValue: '<p>Value-add. Sun Belt. <em>Multifamily.</em></p>',
    },
    'investorsapproach.strategy_intro': {
      label: 'Strategy Intro',
      type: 'richtext',
      defaultValue:
        '<p>For 30 years, we have focused on one thing: creating genuine value for the communities we own — and the people who live and invest in them.</p>',
    },
    'investorsapproach.who_title': {
      label: 'Who We Are Title',
      type: 'richtext',
      defaultValue: '<p>Who We Are</p>',
    },
    'investorsapproach.who_body': {
      label: 'Who We Are Body',
      type: 'richtext',
      defaultValue:
        '<p>A <strong>vertically-integrated, value-add owner-operator</strong> with 30 years of multifamily experience. We own, renovate, and manage every community ourselves — no third-party management, no divided interests.</p>',
    },
    'investorsapproach.what_title': {
      label: 'What We Do Title',
      type: 'richtext',
      defaultValue: '<p>What We Do</p>',
    },
    'investorsapproach.what_body': {
      label: 'What We Do Body',
      type: 'richtext',
      defaultValue:
        '<p>We acquire well-located, <strong>mid-priced multifamily communities</strong> and make them better places to live — through thoughtful renovations, a resident-first approach, and hands-on, in-house operations.</p>',
    },
    'investorsapproach.how_title': {
      label: 'Our Strategy Title',
      type: 'richtext',
      defaultValue: '<p>Our Strategy</p>',
    },
    'investorsapproach.how_body': {
      label: 'Our Strategy Body',
      type: 'richtext',
      defaultValue:
        '<p><strong>Value-add multifamily in high-growth Sun Belt markets</strong>, guided by deep market experience and hyperlocal submarket intelligence. Operation, not allocation: we create value by running the asset — not just funding it.</p>',
    },
  },
  slotGroups: {
    'investorsapproach.portal_cta': portalCtaDef,
    'investorsapproach.strategy_link': strategyLinkDef,
    'investorsapproach.panel_image_1': panelImage1Def,
    'investorsapproach.panel_image_2': panelImage2Def,
    'investorsapproach.panel_image_3': panelImage3Def,
  },
};

(InvestorsApproach as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default InvestorsApproach;
